'use client'

import * as SwitchPrimitives from '@radix-ui/react-switch'
import { cva, type VariantProps } from 'class-variance-authority'
import { Check } from 'lucide-react'
import * as React from 'react'

import { cn } from '../lib/cn'

const switchRootVariants = cva(
  'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=unchecked]:bg-input',
  {
    variants: {
      variant: {
        default: 'data-[state=checked]:bg-primary',
        prime:
          'data-[state=checked]:bg-prime data-[state=checked]:shadow-[0_0_0_3px_oklch(from_var(--prime)_l_c_h_/_0.25)]',
      },
      size: {
        default: 'h-6 w-11',
        sm: 'h-5 w-9',
        lg: 'h-7 w-12',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
)

const switchThumbVariants = cva(
  'pointer-events-none flex items-center justify-center rounded-full bg-card shadow-lg ring-0 transition-transform data-[state=unchecked]:translate-x-0',
  {
    variants: {
      size: {
        default: 'h-5 w-5 data-[state=checked]:translate-x-5',
        sm: 'h-4 w-4 data-[state=checked]:translate-x-4',
        lg: 'h-6 w-6 data-[state=checked]:translate-x-5',
      },
    },
    defaultVariants: { size: 'default' },
  },
)

export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>, VariantProps<typeof switchRootVariants> {
  thumbIcon?: React.ReactNode
}

const Switch = React.forwardRef<React.ComponentRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, variant, size, thumbIcon, ...props }, ref) => (
    <SwitchPrimitives.Root
      ref={ref}
      data-slot='switch'
      className={cn(switchRootVariants({ variant, size, className }))}
      {...props}
    >
      <SwitchPrimitives.Thumb className={cn('group', switchThumbVariants({ size }))}>
        {thumbIcon ?? (
          <Check className='hidden size-4 text-primary group-data-[state=checked]:block ml-[2px]' strokeWidth={3} />
        )}
      </SwitchPrimitives.Thumb>
    </SwitchPrimitives.Root>
  ),
)
Switch.displayName = 'Switch'

export { Switch, switchRootVariants }
